/**
 * EmailPage — state shared-inbox email client.
 *
 * Three views, single route (/email), state-machine within the page:
 *   1) Inbox list — user's accessible state inboxes (state supervisors + admins see all threads for their state; others see assigned-to-me only)
 *   2) Thread list — threads inside a selected state inbox
 *   3) Thread view — full message history + reply box
 *
 * Plus: Admin Queue tab for supervisors + admins (unmatched inbound mail
 * per state, reassignable with one tap).
 *
 * Header slots into the global `usePageHeader` context.
 */
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Mail, ArrowLeft, Search, Inbox, AlertCircle, Paperclip, Send, X, UserPlus, Archive, Loader2, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, Eye, EyeOff, Flag, MailOpen, FileEdit, SendHorizonal, Trash2, Minimize2, Filter, SlidersHorizontal } from 'lucide-react';
import PageLoader from '../components/PageLoader';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { usePageHeader } from '../context/PageHeaderContext';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator';
import SendStatusOverlay from '../components/SendStatusOverlay';
import EmailMessageBody from '../components/EmailMessageBody';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Sheet, SheetContent, SheetTitle } from '../components/ui/sheet';
import BusinessCardContent from '../components/BusinessCardContent';
import OverrideRequestCircle from '../components/OverrideRequestCircle';

const cls = (...xs) => xs.filter(Boolean).join(' ');

// Short relative time
const when = (iso) => {
  if (!iso) return '';
  try {
    const t = new Date(iso);
    const diff = (Date.now() - t.getTime()) / 1000;
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
    return t.toLocaleDateString();
  } catch { return ''; }
};

const initials = (name, fallback) => {
  const src = (name || fallback || '?').trim();
  const parts = src.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || '?';
};

const SignaturePreview = ({ user, onOpenPreview, api }) => {
  // iter175d — Replaced the hardcoded JSX signature preview with one
  // that fetches and renders the canonical backend HTML from
  // /api/me/email-signature. This guarantees ZERO divergence between
  // the in-app email preview and the signature that's actually
  // attached to outbound emails (both call _build_signature_html on
  // the server). Previously the JSX rendition was missing the "Join
  // our Team" CTA pill, the phone-icon row, and the green stroke,
  // which made testadmin's email-page preview look different from
  // their actually-sent emails.
  const [html, setHtml] = React.useState('');
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/me/email-signature', {
          params: { alignment: 'left', theme: 'dark', compat: 'basic' },
        });
        if (alive) setHtml(res?.data?.html || '');
      } catch (_e) { /* fall back to empty preview */ }
    })();
    return () => { alive = false; };
  }, [api, user?.id]);
  if (!html) {
    return (
      <div className="mx-3 my-2 rounded-[14px] h-[160px] bg-[#0f0f0f]/40 animate-pulse" data-testid="email-signature-preview-loading" />
    );
  }
  return (
    <div
      className="mx-3 my-2 cursor-pointer"
      data-testid="email-signature-preview"
      onClick={onOpenPreview}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpenPreview && onOpenPreview(); }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

// Legacy hardcoded preview (kept for reference of what shape the JSX
// component had; superseded by the server-rendered version above).
const SignaturePreviewLegacy_UNUSED = ({ user, onOpenPreview }) => {
  const name = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email || '';
  const role = (user?.role || '').trim();
  const rawState = (user?.state || '').trim();
  const upper = rawState.toUpperCase();
  let stateCode = STATE_CODE_TO_NAME[upper] ? upper : NAME_TO_CODE[rawState.toLowerCase()] || '';
  const stateFull = STATE_CODE_TO_NAME[stateCode] || (STATE_CODE_TO_NAME[upper] ? STATE_CODE_TO_NAME[upper] : rawState);
  const customTitle = user?.is_custom_role && (user?.title || '').trim() ? user.title.trim() : null;
  const roleCap = role ? role.charAt(0).toUpperCase() + role.slice(1) : '';
  const title = customTitle || (stateFull && roleCap ? `${stateFull} ${roleCap}` : roleCap);
  const firstName = (name.split(' ')[0] || 'They').trim();
  const photoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/card/${user?.id || ''}/photo.jpg`;

  const Dots = () => (
    <span className="inline-flex items-center gap-[4px]">
      <span className="w-[5px] h-[5px] rounded-full bg-[#9BAF92] inline-block" />
      <span className="w-[5px] h-[5px] rounded-full bg-[#9BAF92] inline-block" />
      <span className="w-[5px] h-[5px] rounded-full bg-[#9BAF92] inline-block" />
    </span>
  );

  return (
    <div
      className="mx-3 my-2 rounded-[14px] overflow-hidden bg-[#0f0f0f] shadow-md flex"
      data-testid="email-signature-preview"
      style={{ height: 160 }}
    >
      {/* LEFT: portrait */}
      <div className="w-[120px] bg-[#1a1a1a] flex-shrink-0">
        <img
          src={photoUrl}
          alt=""
          className="block w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
        />
      </div>
      {/* RIGHT: content */}
      <div className="flex-1 px-4 py-3 text-white flex flex-col min-w-0">
        <div className="mb-1.5"><Dots /></div>
        <p className="font-black uppercase text-white leading-none tracking-tight truncate"
           style={{ fontSize: 16 }}>
          {name}
        </p>
        <div className="w-7 h-[2px] bg-[#9BAF92] mt-2 mb-1.5" />
        {title && (
          <p className="text-[10px] font-medium text-[#9BAF92] leading-tight truncate">{title}</p>
        )}
        {/* Company — all sage */}
        <p className="text-[9px] text-[#9BAF92] tracking-[0.08em] mt-0.5 mb-2 font-semibold">
          ORB | Collective Solutions
        </p>
        <div>
          <button
            type="button"
            onClick={onOpenPreview}
            className="inline-flex items-center bg-[#586D52] hover:bg-[#4a5d46] text-white rounded-full px-2.5 py-1 text-[9px] font-bold tracking-[0.1em] uppercase transition-colors"
            data-testid="email-sig-preview-btn"
          >
            ▶ Learn about ORB
          </button>
        </div>
        <div className="mt-auto pt-2 flex items-end gap-1.5">
          <p className="text-[8px] text-[#888] leading-tight flex-1 truncate">
            {firstName} is a community member using ORB Uplift.
          </p>
          <Dots />
        </div>
      </div>
    </div>
  );
};

// US state code → full name, matches backend `state_full_name()`
const STATE_CODE_TO_NAME = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',
  CT:'Connecticut',DE:'Delaware',DC:'District of Columbia',FL:'Florida',GA:'Georgia',
  HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',
  LA:'Louisiana',ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',
  MS:'Mississippi',MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',
  NJ:'New Jersey',NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',
  OH:'Ohio',OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',
  SD:'South Dakota',TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',
  WA:'Washington',WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',
};

// Inverse map for stored state values that arrive as full names
const NAME_TO_CODE = Object.fromEntries(
  Object.entries(STATE_CODE_TO_NAME).map(([c, n]) => [n.toLowerCase(), c])
);

// Pitch video asset (shared with BusinessCardContent)
const PITCH_VIDEO = "https://customer-assets.emergentagent.com/job_fb180cb2-6be0-4004-8c69-b9e6acdca6ae/artifacts/6l5vvgs0_3m%20Pitch%20%285%29.mp4";

// Strip HTML tags for readable rendering of inbound message bodies.
// Graph inbound emails often arrive as HTML, which without stripping
// shows raw markup in the thread view. Also collapses 3+ blank lines
// to 2 so signature blocks don't balloon the view.
const stripHtml = (s) => {
  if (!s) return '';
  // Simple detection — if there's no tag, short-circuit.
  if (!/</.test(s)) return s;
  // Replace <br>, <p>, <div>, </li> with linebreaks for readability
  let out = String(s)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n')
    .replace(/<[^>]+>/g, '')         // all remaining tags
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return out;
};

// ─────────── ORB Preview Sheet ───────────
// Renders the pitch video NATIVELY (not via iframe) so the user-
// click that opened this sheet carries through as the user-gesture
// required to autoplay with sound. Chrome/Safari autoplay policy
// blocks unmuted autoplay across iframe boundaries, so we embed
// the <video> directly in the same React tree.

// ─────────── ORB Preview Modal ───────────
// Renders the FULL public business-card UI with the pitch popup
// pre-opened and the video playing — exactly what an external
// recipient experiences when they click the email's CTA. The
// `BusinessCardContent` is always-mounted (CSS-hidden when closed)
// so when the user clicks "Learn about ORB" the parent can call
// `cardRef.current.playPitch()` SYNCHRONOUSLY inside the click
// handler, preserving the user-gesture token required by Chrome/
// Safari for unmuted autoplay.

const OrbPreviewModal = React.forwardRef(({ open, onClose, cardData }, cardRef) => {
  // We always mount BusinessCardContent once cardData is available
  // (CSS-hidden when closed) so the inner <video> element exists
  // before the user clicks "Learn about ORB" — that lets the parent
  // call cardRef.current.playPitch() SYNCHRONOUSLY inside the click
  // handler, preserving the gesture token Chrome/Safari needs for
  // unmuted autoplay.
  return (
    <div
      className={open
        ? 'fixed inset-0 z-[100] bg-black overflow-y-auto flex flex-col items-center'
        : 'fixed left-[-200vw] top-0 w-px h-px overflow-hidden pointer-events-none opacity-0'}
      data-testid="email-orb-preview-modal"
      aria-hidden={!open}
    >
      <div
        className={open
          ? 'fixed top-0 left-0 z-[120]'
          : 'hidden'}
        style={{
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.4375rem)',
          paddingLeft: '25px',
        }}
      >
        <button
          onClick={onClose}
          aria-label="Back to email"
          className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-black/60 backdrop-blur-md text-white hover:bg-black/80 shadow-lg"
          data-testid="email-orb-preview-back"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
      </div>
      {/* The business card lives in its own div so we can reuse all
          its existing styling/animations/popups exactly as external
          recipients see them. */}
      <div
        className="relative w-full flex justify-center pb-2"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}
      >
        {cardData ? (
          <BusinessCardContent
            ref={cardRef}
            cardData={cardData}
            initialPopup={null}
            isVisible={open}
            compact
          />
        ) : (
          <div className="text-white text-center py-20 text-sm">Loading card…</div>
        )}
      </div>
    </div>
  );
});
OrbPreviewModal.displayName = 'OrbPreviewModal';

// ─────────── Contact autocomplete field ───────────
// Wraps a text input with a dropdown of suggested contacts, fetched
// live from /email/contacts/search. Maintains a comma-separated list
// of recipients (the field only autocompletes the *last* token).

const ContactField = ({ value, onChange, placeholder, testId, api, stateCode }) => {
  const [suggestions, setSuggestions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [activeIdx, setActiveIdx] = React.useState(-1);
  const debounce = React.useRef(null);

  // Parse the value into [completed..., currentToken]
  const parse = React.useCallback((v) => {
    const parts = (v || '').split(',');
    const current = parts.pop() ?? '';
    return { completed: parts.map((s) => s.trim()).filter(Boolean), current: current.trimStart() };
  }, []);
  const { completed, current } = parse(value);

  React.useEffect(() => {
    if (!open || !stateCode) return;
    if (debounce.current) clearTimeout(debounce.current);
    const q = current.trim();
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/email/contacts/search`, { params: { state: stateCode, q } });
        setSuggestions(Array.isArray(data) ? data : []);
        setActiveIdx(-1);
      } catch {
        setSuggestions([]);
      }
    }, 120);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [current, stateCode, open, api]);

  const pick = (s) => {
    const next = [...completed, s.email].join(', ') + ', ';
    onChange(next);
    setOpen(true);  // keep open so user can add more
    setSuggestions([]);
    setActiveIdx(-1);
  };

  const onKeyDown = (e) => {
    if (!open || !suggestions.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => (i + 1) % suggestions.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length); }
    else if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); pick(suggestions[activeIdx]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className="relative flex-1 min-w-0">
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="w-full h-8 text-sm bg-transparent border-0 outline-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:outline-none focus-visible:ring-offset-0"
        data-testid={testId}
      />
      {open && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto" data-testid={`${testId}-suggestions`}>
          {suggestions.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(s)}
              className={cls(
                'w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-muted/60',
                i === activeIdx && 'bg-muted/60'
              )}
              data-testid={`${testId}-option-${s.id}`}
            >
              <div className="w-7 h-7 rounded-full bg-[#586D52]/15 text-[#586D52] flex items-center justify-center font-semibold text-[11px] flex-shrink-0">
                {initials(s.name, s.email)}
              </div>
              <div className="min-w-0 flex-1">
                {s.name && <p className="font-medium truncate leading-tight">{s.name}</p>}
                <p className={cls('truncate leading-tight', s.name ? 'text-xs text-muted-foreground' : '')}>{s.email}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────── Compose Sheet ───────────

// iter124 — Swipeable row wrapper for email thread cards. Mirrors
// the ChatPage `SwipeableConvRow` pattern: on touch, dragging left
// reveals an action drawer behind the card. On desktop, where there
// is no touch surface, a small chevron in the bottom-left of the
// card programmatically opens/closes the same drawer.
//
// `actions` is a list of `{ key, icon, label, onClick, bg }`
// objects (left-to-right inside the drawer). The drawer width is
// computed from action count × 72px.
const ACTION_W_PER = 72;
const SwipeableEmailRow = ({ id, actions, offset, setOffset, children, testId }) => {
  const startXRef = React.useRef(null);
  const movedRef = React.useRef(false);
  const totalW = actions.length * ACTION_W_PER;

  const handleTouchStart = (e) => {
    startXRef.current = e.touches[0].clientX;
    movedRef.current = false;
  };
  const handleTouchMove = (e) => {
    if (startXRef.current === null) return;
    const dx = e.touches[0].clientX - startXRef.current;
    if (Math.abs(dx) > 5) movedRef.current = true;
    let newOffset = (offset || 0) + dx;
    if (newOffset > 0) newOffset = 0;
    if (newOffset < -totalW - 30) newOffset = -totalW - 30;
    setOffset(id, newOffset);
  };
  const handleTouchEnd = () => {
    if (!movedRef.current) { startXRef.current = null; return; }
    const cur = offset || 0;
    if (cur < -totalW / 2) setOffset(id, -totalW);
    else setOffset(id, 0);
    startXRef.current = null;
    setTimeout(() => { movedRef.current = false; }, 50);
  };

  return (
    <div className="relative overflow-hidden rounded-2xl mb-2" data-testid={testId}>
      {/* iter125 — Drawer only renders when the card is actually
          slid (offset < 0). When the card is at rest the drawer
          would otherwise peek through the rounded corners as a
          colored sliver. */}
      {(offset || 0) < -0.5 && (
        <div className="absolute inset-y-0 right-0 flex items-stretch rounded-r-2xl overflow-hidden" style={{ width: totalW }}>
          {actions.map((a) => (
            <button
              key={a.key}
              className={cls('flex-col items-center justify-center gap-1 flex text-white text-[10px] font-semibold', a.bg)}
              style={{ width: ACTION_W_PER }}
              onClick={(e) => { e.stopPropagation(); a.onClick(); setOffset(id, 0); }}
              data-testid={`${testId}-action-${a.key}`}
            >
              {a.icon}
              <span>{a.label}</span>
            </button>
          ))}
        </div>
      )}
      {/* The card itself slides over the drawer */}
      <div
        className="relative"
        style={{ transform: `translateX(${offset || 0}px)`, transition: startXRef.current === null ? 'transform 200ms ease' : 'none' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => {
          if (movedRef.current) { e.preventDefault(); e.stopPropagation(); return; }
          if ((offset || 0) < 0) { setOffset(id, 0); e.preventDefault(); e.stopPropagation(); }
        }}
      >
        {children}
        {/* iter125 — Desktop reveal-trigger: bare ChevronRight arrow
            in the bottom-left corner of the card. No background pill. */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            const cur = offset || 0;
            setOffset(id, cur === 0 ? -totalW : 0);
          }}
          className="hidden md:flex absolute bottom-1.5 left-1.5 text-[#586D52]/70 hover:text-[#586D52] items-center justify-center"
          aria-label="Show actions"
          data-testid={`${testId}-chevron`}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// iter123 — Outlined pill row for From/To/Cc/Bcc/Subject. Replaces
// the previous filled-pill rows that the user said felt heavy.
// `addressMode=true` enables the "expand to see all typed addresses"
// chevron when 2+ comma-separated emails are present.
//
// iter124 — All pills now share the EXACT same dimensions so From,
// To, Subject, and the body box all line up at uniform height:
//   px-3 py-1 rounded-xl border min-h-[40px]
// Inputs inside use `focus:outline-none focus:ring-0 focus-visible:ring-0`
// to kill the browser/yellow focus ring; the surrounding pill border
// is the only visual focus indicator.
const PillField = ({
  label,
  children,
  addressValue,
  expanded,
  onToggleExpand,
  testId,
}) => {
  const addrs = addressMode(addressValue);
  const showChevron = !!addressValue && addrs.length > 1;
  return (
    <div className="space-y-1 min-w-0">
      <div
        className="flex items-center gap-2 px-3 py-1 rounded-xl border border-[#586D52]/40 dark:border-[#586D52]/50 bg-transparent min-h-[40px]"
        data-testid={testId}
      >
        <span className="text-xs font-semibold text-[#586D52] dark:text-[#9aab92] w-10 flex-shrink-0">{label}</span>
        <div className="flex-1 min-w-0">{children}</div>
        {showChevron && (
          <button
            type="button"
            onClick={onToggleExpand}
            className="p-1 -m-1 rounded-full hover:bg-[#586D52]/10 flex-shrink-0"
            aria-label="Show all addresses"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5 text-[#586D52]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#586D52]" />}
          </button>
        )}
      </div>
      {showChevron && expanded && (
        <div className="px-4 py-1.5 ml-3 mr-1 rounded-xl border border-[#586D52]/20 bg-[#586D52]/5 dark:bg-[#586D52]/10 space-y-0.5">
          {addrs.map((a, i) => (
            <p key={`${a}-${i}`} className="text-xs font-mono text-muted-foreground truncate">{a}</p>
          ))}
        </div>
      )}
    </div>
  );
};

// Compact From chip — visually IDENTICAL to PillField so all rows
// line up at the same height. Used when From sits inline with To.
const FromChip = ({ inboxAddress, className = '' }) => (
  <div className={cls(
    'flex items-center gap-2 px-3 py-1 rounded-xl border border-[#586D52]/40 dark:border-[#586D52]/50 bg-transparent min-h-[40px] min-w-0 flex-shrink-0',
    className,
  )}>
    <span className="text-xs font-semibold text-[#586D52] dark:text-[#9aab92] flex-shrink-0">From</span>
    <span className="text-xs font-mono truncate text-muted-foreground">{inboxAddress}</span>
  </div>
);

// Split comma-separated address string into individual non-empty entries.
const addressMode = (v) => (v || '')
  .split(',')
  .map((s) => s.trim())
  .filter(Boolean);

// iter123 — Compact signature card. Default state: a tiny pill in
// the lower-right corner of the body editor. When clicked, expands
// to the full SignaturePreview with a collapse button overlaid.
// What the recipient actually receives (the rendered HTML signature
// from the backend) is UNCHANGED — this only affects the in-app
// preview size.
const CompactSignature = ({ user, onOpenPreview, expanded, setExpanded, api }) => {
  if (!expanded) {
    return (
      <div className="flex justify-end px-3 pb-1">
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#0f0f0f] text-white hover:bg-[#1a1a1a] text-[10px] font-semibold tracking-wide shadow-sm"
          data-testid="email-sig-compact-toggle"
        >
          <span className="inline-flex items-center gap-[3px]">
            <span className="w-[3px] h-[3px] rounded-full bg-[#9BAF92] inline-block" />
            <span className="w-[3px] h-[3px] rounded-full bg-[#9BAF92] inline-block" />
            <span className="w-[3px] h-[3px] rounded-full bg-[#9BAF92] inline-block" />
          </span>
          Signature
        </button>
      </div>
    );
  }
  return (
    <div className="relative" data-testid="email-sig-expanded">
      <SignaturePreview user={user} onOpenPreview={onOpenPreview} api={api} />
      <button
        type="button"
        onClick={() => setExpanded(false)}
        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black/90 z-10"
        aria-label="Collapse signature"
        data-testid="email-sig-collapse"
      >
        <Minimize2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const ComposeSheet = ({ open, onClose, api, stateCode, inboxAddress, replyTo, onSent, user, editingDraft, onDraftChanged, inline = false }) => {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]); // [{id, file_name, ...}]
  const [sending, setSending] = useState(false);
  // 'success' | 'error' | null — drives the full-screen post-send overlay
  const [sendStatus, setSendStatus] = useState(null);
  const [orbPreviewOpen, setOrbPreviewOpen] = useState(false);
  // iter123 — UI toggles for the redesigned header. CC/BCC are
  // hidden by default behind a "Cc/Bcc" chevron toggle. The
  // signature card sits collapsed in the lower-right corner until
  // the user clicks it open.
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [signatureExpanded, setSignatureExpanded] = useState(false);
  // Which field's full address list is currently expanded (To / CC /
  // BCC). Null when collapsed. Used by the inline chevron to surface
  // every typed-in address without forcing the user to scroll
  // horizontally through a long comma-separated string.
  const [expandedAddressField, setExpandedAddressField] = useState(null);
  // Draft id for the autosave loop. Server-issued; null until the
  // first PUT/POST. `draftRef` mirrors it so async timers see the
  // latest value without needing a re-render.
  const [draftId, setDraftId] = useState(null);
  const draftRef = useRef(null);
  const lastSavedRef = useRef('');  // last-saved JSON snapshot — skip duplicate writes
  // Public business-card data — same payload an external recipient
  // sees when they hit /card/{user_id}. Pre-fetched so the pitch
  // popup can be opened SYNCHRONOUSLY inside the user-gesture click
  // handler (required for unmuted autoplay).
  const [cardData, setCardData] = useState(null);
  const cardRef = React.useRef(null);

  useEffect(() => {
    if (!open || !user?.id || cardData) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/card/${user.id}`);
        if (!cancelled) setCardData(data);
      } catch (_e) {
        // fall back to a minimal local object so the card still renders
        if (!cancelled) setCardData({
          id: user.id,
          name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'You',
          title: user.title || user.role || '',
          email: '',
          phone: '',
          profile_picture: user.profile_picture || '',
        });
      }
    })();
    return () => { cancelled = true; };
  }, [open, user?.id, cardData, api, user]);

  // Synchronous click handler — invoke the imperative handle on
  // BusinessCardContent so the <video>.play() call happens INSIDE
  // the user-gesture call stack. This is the only reliable way to
  // autoplay unmuted on Chrome/Safari.
  const openOrbPreview = () => {
    // Dynamic: BusinessCardContent decides whether to open the pitch
    // (normal) or CAPs (inactive/disabled) popup based on the
    // current admin-set pitch_state.
    cardRef.current?.openLearnAboutOrb();
    setOrbPreviewOpen(true);
  };

  const closeOrbPreview = () => {
    cardRef.current?.pausePitch();
    cardRef.current?.closePopup();
    setOrbPreviewOpen(false);
  };

  // Open: hydrate fields from a reply OR a draft we're resuming.
  // Cleanly reset draft-tracking refs every open so a fresh compose
  // doesn't reuse the previous draft's id.
  // NOTE — deps are STRING values (not the parent-rendered object
  // references) so the parent re-rendering with a new `replyTo={...}`
  // object literal does NOT wipe the user's typed-in To/CC/body.
  // Without this, every parent re-render reset To back to
  // replyTo.external_email, so typing a new recipient was silently
  // overwritten on the next render.
  const replyThreadKey = replyTo?.thread_id || '';
  const replyExternalKey = replyTo?.external_email || '';
  const replySubjectKey = replyTo?.subject || '';
  const editingDraftKey = editingDraft?.id || '';
  useEffect(() => {
    if (!open) return;
    if (editingDraft) {
      setTo(editingDraft.to || '');
      setCc(editingDraft.cc || '');
      setBcc(editingDraft.bcc || '');
      setSubject(editingDraft.subject || '');
      setBody(editingDraft.body || '');
      setAttachments([]);
      setDraftId(editingDraft.id);
      draftRef.current = editingDraft.id;
      // If the draft had CC or BCC, expand the toggle so the user
      // sees those rows immediately on re-open.
      setShowCcBcc(!!(editingDraft.cc || editingDraft.bcc));
      lastSavedRef.current = JSON.stringify({
        to: editingDraft.to || '',
        cc: editingDraft.cc || '',
        bcc: editingDraft.bcc || '',
        subject: editingDraft.subject || '',
        body: editingDraft.body || '',
      });
    } else if (replyTo) {
      // Trailing comma+space so the user can immediately type a 2nd
      // recipient without manually typing a separator first.
      setTo(replyTo.external_email ? `${replyTo.external_email}, ` : '');
      setCc('');
      setBcc('');
      setSubject(replyTo.subject?.startsWith('Re:') ? replyTo.subject : `Re: ${replyTo.subject || ''}`);
      setBody('');
      setAttachments([]);
      setDraftId(null);
      draftRef.current = null;
      setShowCcBcc(false);
      lastSavedRef.current = '';
    } else {
      setTo(''); setCc(''); setBcc(''); setSubject(''); setBody(''); setAttachments([]);
      setDraftId(null);
      draftRef.current = null;
      setShowCcBcc(false);
      lastSavedRef.current = '';
    }
    setExpandedAddressField(null);
    setSignatureExpanded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, replyThreadKey, replyExternalKey, replySubjectKey, editingDraftKey]);

  // Autosave to drafts every ~2s of inactivity. Skip when the
  // compose is empty (no point writing an empty draft) and when
  // nothing has changed since the last save.
  useEffect(() => {
    if (!open) return undefined;
    const handle = setTimeout(async () => {
      const isEmpty = !to.trim() && !cc.trim() && !bcc.trim() && !subject.trim() && !body.trim();
      if (isEmpty) return;
      const snapshot = JSON.stringify({ to, cc, bcc, subject, body, attachment_ids: attachments.map((a) => a.id) });
      if (snapshot === lastSavedRef.current) return;
      try {
        const payload = {
          state: stateCode,
          to, cc, bcc, subject, body,
          thread_id: replyTo?.thread_id || null,
          attachment_ids: attachments.map((a) => a.id),
        };
        if (draftRef.current) {
          await api.put(`/email/drafts/${draftRef.current}`, payload);
        } else {
          const { data } = await api.post('/email/drafts', payload);
          draftRef.current = data.id;
          setDraftId(data.id);
        }
        lastSavedRef.current = snapshot;
        onDraftChanged?.();
      } catch (_e) { /* silent */ }
    }, 500);
    return () => clearTimeout(handle);
  }, [open, to, cc, bcc, subject, body, stateCode, replyTo?.thread_id, attachments, api, onDraftChanged]);

  const uploadFile = async (f) => {
    const fd = new FormData();
    fd.append('file', f);
    try {
      const { data } = await api.post('/email/attachments/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAttachments((xs) => [...xs, data]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    }
  };

  const handleFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(uploadFile);
    e.target.value = '';
  };

  const send = async () => {
    const toList = to.split(',').map((s) => s.trim()).filter(Boolean);
    const ccList = cc.split(',').map((s) => s.trim()).filter(Boolean);
    const bccList = bcc.split(',').map((s) => s.trim()).filter(Boolean);
    if (!toList.length) { toast.error('Add a recipient'); return; }
    if (!subject.trim()) { toast.error('Add a subject'); return; }
    if (!body.trim()) { toast.error('Write something'); return; }
    setSending(true);
    try {
      const payload = {
        state: stateCode,
        to: toList,
        cc: ccList,
        bcc: bccList,
        subject: subject.trim(),
        body: body.trim(),
        attachment_ids: attachments.map((a) => a.id),
        draft_id: draftRef.current || null,
      };
      if (replyTo?.thread_id) payload.thread_id = replyTo.thread_id;
      const { data } = await api.post('/email/send', payload);
      setSendStatus('success');
      // Clear local draft tracking so a new compose starts fresh.
      draftRef.current = null;
      setDraftId(null);
      lastSavedRef.current = '';
      onDraftChanged?.();
      onSent?.(data);
      // Delay closing so the green ✓ has time to display.
      setTimeout(() => onClose(), 900);
    } catch (e) {
      setSendStatus('error');
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  // iter122 — Inline rendering mode for the desktop split-pane.
  // Wraps the same form internals in a plain div so the composer
  // sits INSIDE the right-pane email view instead of slamming up
  // from the bottom as a sheet.
  // iter123 — In compose mode (no replyTo) the inline card stretches
  // to fill the right pane (`h-full`) so the user has the full
  // vertical room to write. Reply mode stays compact so the message
  // history above stays visible.
  if (inline) {
    if (!open) return null;
    return (
      <div className={cls(
        'border border-[#586D52]/30 rounded-2xl bg-white dark:bg-[#141414] shadow-md overflow-hidden flex flex-col',
        !replyTo ? 'h-full' : 'mt-3',
      )} data-testid="email-compose-inline">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-[#586D52]/5 flex-shrink-0">
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-muted" data-testid="email-compose-inline-close"><X className="w-4 h-4" /></button>
            <p className="text-sm font-semibold">{replyTo ? 'Reply' : 'New message'}</p>
          </div>
        </div>
        {/* iter123 — From + To live on the same outlined-pill row.
            Cc/Bcc are hidden behind a chevron toggle below.
            iter124 — From uses FromChip so it visually matches the
            other pill rows exactly (same height, same border). */}
        <div className="flex-shrink-0 px-3 pt-3 pb-1 space-y-1.5">
          <div className="flex gap-2 items-start min-w-0">
            <FromChip inboxAddress={inboxAddress} className="max-w-[40%]" />
            <div className="flex-1 min-w-0">
              <PillField
                label="To"
                addressValue={to}
                expanded={expandedAddressField === 'to'}
                onToggleExpand={() => setExpandedAddressField((x) => x === 'to' ? null : 'to')}
                testId="email-compose-inline-to-pill"
              >
                <ContactField value={to} onChange={setTo} placeholder="name@example.com" testId="email-compose-inline-to" api={api} stateCode={stateCode} />
              </PillField>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowCcBcc((v) => !v)}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#586D52] dark:text-[#9aab92] hover:underline px-1 ml-1"
            data-testid="email-compose-inline-cc-bcc-toggle"
          >
            {showCcBcc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Cc/Bcc
          </button>
          {showCcBcc && (
            <>
              <PillField
                label="Cc"
                addressValue={cc}
                expanded={expandedAddressField === 'cc'}
                onToggleExpand={() => setExpandedAddressField((x) => x === 'cc' ? null : 'cc')}
                testId="email-compose-inline-cc-pill"
              >
                <ContactField value={cc} onChange={setCc} placeholder="cc@example.com" testId="email-compose-inline-cc" api={api} stateCode={stateCode} />
              </PillField>
              <PillField
                label="Bcc"
                addressValue={bcc}
                expanded={expandedAddressField === 'bcc'}
                onToggleExpand={() => setExpandedAddressField((x) => x === 'bcc' ? null : 'bcc')}
                testId="email-compose-inline-bcc-pill"
              >
                <ContactField value={bcc} onChange={setBcc} placeholder="bcc@example.com" testId="email-compose-inline-bcc" api={api} stateCode={stateCode} />
              </PillField>
            </>
          )}
          <PillField label="Subject" testId="email-compose-inline-subject-pill">
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="w-full h-7 text-sm bg-transparent border-0 outline-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:outline-none focus-visible:ring-offset-0" data-testid="email-compose-inline-subject" />
          </PillField>
        </div>
        {/* iter124 — Body wrapped in an outlined card that matches
            the From/To/Subject pill styling. Inputs use
            focus-visible:ring-0 so the browser's yellow ring is
            replaced by just the cursor (caret). */}
        <div className="px-3 pt-1 pb-2 flex-1 flex flex-col min-h-0">
          <div className="flex-1 flex flex-col rounded-xl border border-[#586D52]/40 dark:border-[#586D52]/50 p-3 min-h-0 relative">
            <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your message…" className="w-full flex-1 min-h-[120px] text-sm bg-transparent border-0 outline-none resize-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:outline-none focus-visible:ring-offset-0" data-testid="email-compose-inline-body" />
            <CompactSignature user={user} onOpenPreview={openOrbPreview} expanded={signatureExpanded} setExpanded={setSignatureExpanded} api={api} />
          </div>
        </div>
        <div className="flex items-center justify-between px-3 py-2 border-t gap-2 bg-muted/30 flex-shrink-0">
          <label className="inline-flex items-center gap-1.5 text-xs text-[#586D52] cursor-pointer px-2 py-2 rounded hover:bg-muted" data-testid="email-compose-inline-attach-label">
            <Paperclip className="w-4 h-4" />
            <span>{attachments.length ? `${attachments.length}` : 'Attach'}</span>
            <input type="file" className="hidden" multiple onChange={handleFilePick} data-testid="email-compose-inline-attach-input" />
          </label>
          {attachments.length > 0 && (
            <p className="text-[11px] text-muted-foreground flex-1 truncate">
              {attachments.length} attachment{attachments.length === 1 ? '' : 's'}
            </p>
          )}
          <Button onClick={send} disabled={sending} className="bg-[#586D52] hover:bg-[#4a5d46]" size="sm" data-testid="email-compose-inline-send">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Send</>}
          </Button>
        </div>
        <SendStatusOverlay status={sendStatus} onDone={() => setSendStatus(null)} />
      </div>
    );
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="bottom" className="h-[83vh] p-0 flex flex-col overflow-hidden rounded-t-3xl border-t-0 bg-white dark:bg-[#1f1f1f] [&>button.absolute]:hidden" data-testid="email-compose-sheet">
        <SheetTitle className="sr-only">{replyTo ? 'Reply to email' : 'Compose new email'}</SheetTitle>
        {/* Title bar — compact, just close */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/5 dark:border-white/5 flex-shrink-0">
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-muted" data-testid="email-compose-close"><X className="w-4 h-4" /></button>
            <p className="text-sm font-semibold">{replyTo ? 'Reply' : 'New message'}</p>
          </div>
        </div>

        {/* iter123 — From + To on a single row, outlined pills.
            Cc/Bcc hidden behind a chevron toggle. Subject below.
            iter124 — From uses FromChip so it matches the other
            pill rows; mobile cap is 35% so the To input has more
            room for visible addresses. */}
        <div className="flex-shrink-0 px-3 pt-3 pb-1 space-y-1.5">
          <div className="flex gap-2 items-start min-w-0">
            <FromChip inboxAddress={inboxAddress} className="max-w-[35%]" />
            <div className="flex-1 min-w-0">
              <PillField
                label="To"
                addressValue={to}
                expanded={expandedAddressField === 'to'}
                onToggleExpand={() => setExpandedAddressField((x) => x === 'to' ? null : 'to')}
                testId="email-compose-to-pill"
              >
                <ContactField
                  value={to}
                  onChange={setTo}
                  placeholder="name@example.com"
                  testId="email-compose-to"
                  api={api}
                  stateCode={stateCode}
                />
              </PillField>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowCcBcc((v) => !v)}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#586D52] dark:text-[#9aab92] hover:underline px-1 ml-1"
            data-testid="email-compose-cc-bcc-toggle"
          >
            {showCcBcc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Cc/Bcc
          </button>
          {showCcBcc && (
            <>
              <PillField
                label="Cc"
                addressValue={cc}
                expanded={expandedAddressField === 'cc'}
                onToggleExpand={() => setExpandedAddressField((x) => x === 'cc' ? null : 'cc')}
                testId="email-compose-cc-pill"
              >
                <ContactField value={cc} onChange={setCc} placeholder="cc@example.com" testId="email-compose-cc" api={api} stateCode={stateCode} />
              </PillField>
              <PillField
                label="Bcc"
                addressValue={bcc}
                expanded={expandedAddressField === 'bcc'}
                onToggleExpand={() => setExpandedAddressField((x) => x === 'bcc' ? null : 'bcc')}
                testId="email-compose-bcc-pill"
              >
                <ContactField value={bcc} onChange={setBcc} placeholder="bcc@example.com" testId="email-compose-bcc" api={api} stateCode={stateCode} />
              </PillField>
            </>
          )}
          <PillField label="Subject" testId="email-compose-subject-pill">
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="w-full h-7 text-sm bg-transparent border-0 outline-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:outline-none focus-visible:ring-offset-0"
              data-testid="email-compose-subject"
            />
          </PillField>
        </div>

        {/* Message body — shrinks to leave room for sig preview */}
        <div className="flex-1 overflow-y-auto flex flex-col px-3 pt-1 pb-2">
          {/* iter124 — Outlined body box, matches From/To/Subject. */}
          <div className="flex-1 flex flex-col rounded-xl border border-[#586D52]/40 dark:border-[#586D52]/50 p-3 min-h-[160px]">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your message…"
              className="w-full flex-1 h-full min-h-[140px] text-sm bg-transparent border-0 outline-none resize-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:outline-none focus-visible:ring-offset-0"
              data-testid="email-compose-body"
            />
            {/* iter123 — Compact signature: tiny pill by default,
                expand to full preview on click, collapse to shrink. */}
            <CompactSignature user={user} onOpenPreview={openOrbPreview} expanded={signatureExpanded} setExpanded={setSignatureExpanded} api={api} />
          </div>
        </div>

        {/* Attachments row (only when present) */}
        {attachments.length > 0 && (
          <div className="px-3 py-1.5 flex-shrink-0 border-t space-y-1 max-h-[120px] overflow-y-auto">
            {attachments.map((a) => (
              <div key={a.id} className="flex items-center justify-between px-2 py-1 rounded bg-muted/60 text-xs">
                <div className="flex items-center gap-2 truncate">
                  <Paperclip className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{a.file_name}</span>
                  <span className="text-muted-foreground flex-shrink-0">({Math.round(a.size_bytes / 1024)} KB)</span>
                </div>
                <button onClick={() => setAttachments(xs => xs.filter(x => x.id !== a.id))} className="text-muted-foreground hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}

        {/* Fixed bottom action bar — attach + save draft + send */}
        <div className="flex items-center gap-2 px-3 py-2.5 border-t bg-background flex-shrink-0" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 10px)' }}>
          <label className="inline-flex items-center gap-1.5 text-xs text-[#586D52] cursor-pointer px-2 py-2 rounded hover:bg-muted" data-testid="email-compose-attach-label">
            <Paperclip className="w-4 h-4" />
            <span>{attachments.length ? `${attachments.length}` : ''}</span>
            <input type="file" className="hidden" multiple onChange={handleFilePick} data-testid="email-compose-attach-input" />
          </label>
          <Button
            type="button"
            variant="outline"
            disabled={sending || (!to.trim() && !cc.trim() && !subject.trim() && !body.trim())}
            onClick={async () => {
              // Force-flush the current compose state into a draft and
              // close the sheet. Bypasses the 2s debounce so the user
              // gets immediate confirmation.
              try {
                const payload = {
                  state: stateCode,
                  to, cc, subject, body,
                  thread_id: replyTo?.thread_id || null,
                  attachment_ids: attachments.map((a) => a.id),
                };
                if (draftRef.current) {
                  await api.put(`/email/drafts/${draftRef.current}`, payload);
                } else {
                  const { data } = await api.post('/email/drafts', payload);
                  draftRef.current = data.id;
                  setDraftId(data.id);
                }
                lastSavedRef.current = JSON.stringify({ to, cc, subject, body, attachment_ids: attachments.map((a) => a.id) });
                onDraftChanged?.();
                toast.success('Saved to Drafts');
                onClose();
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Could not save draft');
              }
            }}
            className="h-11 px-3 text-sm font-semibold"
            data-testid="email-compose-save-draft"
          >
            <FileEdit className="w-4 h-4 mr-1.5" /> Save Draft
          </Button>
          <Button
            onClick={send}
            disabled={sending}
            className="flex-1 h-11 bg-[#586D52] hover:bg-[#4a5d46] text-base font-semibold"
            data-testid="email-compose-send"
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-5 h-5 mr-2" />Send</>}
          </Button>
        </div>
        <OrbPreviewModal
          ref={cardRef}
          open={orbPreviewOpen}
          onClose={closeOrbPreview}
          cardData={cardData}
        />
      </SheetContent>
      {/* Full-screen ✓ / ✕ overlay shown for ~1.5s after a send.
          Lives outside SheetContent so the dimmer covers the whole
          viewport, not just the sheet. */}
      <SendStatusOverlay status={sendStatus} onDone={() => setSendStatus(null)} />
    </Sheet>
  );
};

// ─────────── Reassign Sheet ───────────

const ReassignSheet = ({ open, onClose, api, threadId, stateCode, currentUserId, onReassigned }) => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  // iter125.2 — Detect desktop landscape so we can render this as a
  // bottom-right corner "chat-box" popup on desktop instead of a
  // full-width centered bottom sheet.
  const [isDesk, setIsDesk] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 768px) and (orientation: landscape)').matches
  );
  // ORB embed: the bottom nav stays mounted on desktop, so this
  // popup needs to (a) sit above the nav's z-50 and (b) clear the
  // nav's height when positioning from `bottom`.
  const [isOrbEmbed] = useState(() => {
    try { return window.self !== window.top; } catch { return false; }
  });
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px) and (orientation: landscape)');
    const handler = (e) => setIsDesk(e.matches);
    mq.addEventListener?.('change', handler) || mq.addListener?.(handler);
    return () => {
      mq.removeEventListener?.('change', handler) || mq.removeListener?.(handler);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get('/tasks/assignable-users');
        // Endpoint returns { users: [...] } — unwrap defensively in
        // case the shape ever changes.
        const list = Array.isArray(data) ? data : Array.isArray(data?.users) ? data.users : [];
        setUsers(list);
      } catch {/* silent */}
    })();
  }, [open, api]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter((u) =>
      `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase().includes(s)
    );
  }, [users, q]);

  const pick = async (uid) => {
    setBusy(true);
    try {
      await api.post(`/email/threads/${threadId}/assign`, { user_id: uid });
      toast.success('Thread reassigned');
      onReassigned?.(uid);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reassign failed');
    } finally {
      setBusy(false);
    }
  };

  // iter125.2 — Shared body so the desktop popup and mobile sheet
  // render identical content (outlined user rows, no divide-y lines).
  const Body = (
    <>
      <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
        <p className="font-semibold text-sm">Reassign thread</p>
        <button onClick={onClose} className="p-1.5 rounded-full hover:bg-muted" data-testid="email-reassign-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="px-3 pb-2 flex-shrink-0">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="pl-9 h-9" data-testid="email-reassign-search" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-2">
        {filtered.map((u) => (
          <button
            key={u.id}
            disabled={busy}
            onClick={() => pick(u.id)}
            className={cls(
              // iter125.2 — Outlined card per user row (matches the
              // EmailPage thread cards). No more divide-y dividers.
              'w-full px-3 py-3 flex items-center gap-3 rounded-2xl border shadow-sm text-left transition-colors',
              u.id === currentUserId
                ? 'bg-[#586D52]/5 border-[#586D52]/40'
                : 'bg-white hover:bg-white dark:bg-[#1a1a1a] dark:hover:bg-[#222] border-black/10 dark:border-white/10',
            )}
            data-testid={`email-reassign-user-${u.id}`}
          >
            <div className="w-9 h-9 rounded-full bg-[#586D52]/15 text-[#586D52] flex items-center justify-center font-semibold text-sm flex-shrink-0">
              {initials(`${u.first_name || ''} ${u.last_name || ''}`, u.email)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{u.first_name || ''} {u.last_name || ''}</p>
              <p className="text-xs text-muted-foreground truncate">{u.email}</p>
            </div>
            {u.id === currentUserId && <span className="text-[10px] uppercase font-semibold text-[#586D52]">current</span>}
          </button>
        ))}
        {!filtered.length && <p className="text-center text-sm text-muted-foreground py-8">No users found</p>}
      </div>
    </>
  );

  // ─── DESKTOP: right-corner chat-box popup, NOT a centered sheet
  if (isDesk) {
    if (!open) return null;
    // ORB embed: clear the bottom nav (h-16 = 64px) + the original
    // 24px breathing-room gap = 88px.
    const bottomOffset = isOrbEmbed ? 88 : 24;
    return (
      <>
        {/* Click-away backdrop (transparent — desktop popups don't
            dim the page, they just close on outside click). */}
        <div
          className="fixed inset-0 z-[60]"
          onClick={onClose}
          data-testid="email-reassign-backdrop"
        />
        <div
          className="fixed z-[61] flex flex-col rounded-2xl shadow-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#1f1f1f] overflow-hidden"
          style={{
            right: '24px',
            bottom: `${bottomOffset}px`,
            width: '380px',
            maxHeight: '70vh',
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid="email-reassign-popup-desktop"
        >
          {Body}
        </div>
      </>
    );
  }

  // ─── MOBILE: rounded-top bottom sheet, dark gray background
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="bottom"
        className="h-[83vh] p-0 flex flex-col overflow-hidden rounded-t-3xl border-t-0 bg-white dark:bg-[#1f1f1f] [&>button.absolute]:hidden"
        data-testid="email-reassign-sheet"
      >
        <SheetTitle className="sr-only">Reassign thread</SheetTitle>
        {Body}
      </SheetContent>
    </Sheet>
  );
};

// ─────────── Main Page ───────────

export default function EmailPage({ forceStateCode = null, hideInboxList = false, includeSystem = false }) {
  const { api, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [inboxes, setInboxes] = useState(null);
  const [inboxSearch, setInboxSearch] = useState('');
  const [threadSearch, setThreadSearch] = useState('');
  const [selectedState, setSelectedState] = useState(forceStateCode || searchParams.get('state') || null);
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [showQueue, setShowQueue] = useState(searchParams.get('queue') === '1');
  const [showMonitor, setShowMonitor] = useState(searchParams.get('monitor') === '1');
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgsLoading, setMsgsLoading] = useState(false);

  const [composeOpen, setComposeOpen] = useState(false);
  const [composeReplyTo, setComposeReplyTo] = useState(null);
  const [composeDraft, setComposeDraft] = useState(null);
  const [reassignOpen, setReassignOpen] = useState(false);

  // Folder selector — 'inbox' (default) | 'sent' | 'drafts'.
  // Per-user view of their own sends/drafts within the current state.
  const [folder, setFolder] = useState('inbox');
  const [drafts, setDrafts] = useState([]);

  // iter124 — Filter pills (Unread / Hidden / Flagged). Clicking the
  // funnel icon next to the search bar swaps the bar for these 3
  // chips. Toggling any chip narrows the visible threads inside the
  // current folder. State persisted in localStorage so the user's
  // hidden + flagged thread ids survive across sessions.
  const [filterMode, setFilterMode] = useState('search'); // 'search' | 'filter'
  const [filterUnread, setFilterUnread] = useState(false);
  const [filterHidden, setFilterHidden] = useState(false);
  const [filterFlagged, setFilterFlagged] = useState(false);
  const [hiddenThreadIds, setHiddenThreadIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('orb_email_hidden_ids') || '[]')); }
    catch { return new Set(); }
  });
  const [flaggedThreadIds, setFlaggedThreadIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('orb_email_flagged_ids') || '[]')); }
    catch { return new Set(); }
  });
  const [readOverrides, setReadOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem('orb_email_read_overrides') || '{}'); }
    catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem('orb_email_hidden_ids', JSON.stringify([...hiddenThreadIds])); } catch {}
  }, [hiddenThreadIds]);
  useEffect(() => {
    try { localStorage.setItem('orb_email_flagged_ids', JSON.stringify([...flaggedThreadIds])); } catch {}
  }, [flaggedThreadIds]);
  useEffect(() => {
    try { localStorage.setItem('orb_email_read_overrides', JSON.stringify(readOverrides)); } catch {}
  }, [readOverrides]);

  // iter124 — Per-card swipe offset (px). Negative = drawer revealed.
  // Same state shape as ChatPage's swipeOffsets — by-row id → number.
  const [swipeOffsets, setSwipeOffsets] = useState({});
  const setSwipeOffset = useCallback((rid, off) => {
    setSwipeOffsets((s) => ({ ...s, [rid]: off }));
  }, []);
  const toggleHidden = useCallback((tid) => {
    setHiddenThreadIds((s) => {
      const next = new Set(s);
      if (next.has(tid)) next.delete(tid); else next.add(tid);
      return next;
    });
  }, []);
  const toggleFlagged = useCallback((tid) => {
    setFlaggedThreadIds((s) => {
      const next = new Set(s);
      if (next.has(tid)) next.delete(tid); else next.add(tid);
      return next;
    });
  }, []);
  const toggleReadOverride = useCallback((tid, threadUnread) => {
    // Local-only read/unread toggle: stores `true` = force read,
    // `false` = force unread, undefined = follow server state.
    setReadOverrides((m) => {
      const next = { ...m };
      const currentlyReadEffective = next[tid] === true || (next[tid] !== false && !threadUnread);
      if (currentlyReadEffective) {
        next[tid] = false; // mark unread
      } else {
        next[tid] = true; // mark read
      }
      return next;
    });
  }, []);

  // iter122 — Desktop split-pane email layout. On landscape desktop
  // the thread list (2/6 of viewport) sits next to the open thread
  // (4/6). On mobile / iPad portrait we keep the legacy push-stack
  // behavior. Tracked here via a CSS media query so a window resize
  // re-renders the layout.
  const [isDesktopLandscape, setIsDesktopLandscape] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(min-width: 768px) and (orientation: landscape)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(min-width: 768px) and (orientation: landscape)');
    const handler = (e) => setIsDesktopLandscape(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  // iter122 — Desktop-only left-pane collapse state. The toggle pill
  // sits anchored on the left edge of the right pane so the user can
  // always pull the thread list back open. Persisted so the choice
  // sticks across page navigations.
  const [leftPaneCollapsed, setLeftPaneCollapsed] = useState(
    () => localStorage.getItem('orb_email_left_collapsed') === '1',
  );
  useEffect(() => {
    localStorage.setItem('orb_email_left_collapsed', leftPaneCollapsed ? '1' : '0');
  }, [leftPaneCollapsed]);

  // iter122 — Inline reply state. When the user clicks Reply, the
  // composer renders INSIDE the right-pane email view (not a sheet).
  // Resets whenever the selected thread changes.
  const [inlineReplyOpen, setInlineReplyOpen] = useState(false);

  const selectedInbox = useMemo(
    () => inboxes?.find((i) => i.state === selectedState) || null,
    [inboxes, selectedState]
  );

  const canViewAll = useMemo(() => {
    if (!user || !selectedInbox) return false;
    if (user.role === 'admin') return true;
    if (user.role === 'supervisor' && (user.state || '').toUpperCase() === selectedInbox.state) return true;
    // Non-admin users with the explicit admin_email_queue toggle get
    // access to the unmatched-queue tab in the inbox header.
    if (user.feature_toggles?.admin_email_queue === true) return true;
    return false;
  }, [user, selectedInbox]);

  // Resolve the state's full human-readable name for use in titles.
  const selectedStateFull = selectedState
    ? (STATE_CODE_TO_NAME[selectedState] || selectedState)
    : '';

  const canMonitor = useMemo(() => !!selectedInbox?.can_monitor, [selectedInbox]);

  // iter124 — Leads / affiliates only ever see ONE inbox (their own
  // state's). Rendering "North Carolina Inbox" up top is just noise
  // for them — the page itself IS the inbox. Show "Email" instead,
  // and let the title flip to the message subject when a thread is
  // opened. Admins/supervisors keep the per-state label since they
  // can rotate through multiple inboxes.
  const isSingleInboxUser = user?.role !== 'admin' && user?.role !== 'supervisor' && (inboxes?.length || 0) <= 1;

  // Header — dynamic based on view
  usePageHeader({
    title: selectedThread
      ? (selectedThread.subject || '(no subject)')
      : (selectedState
          ? (showMonitor ? `${selectedStateFull} Monitor` : showQueue ? `${selectedStateFull} Queue` : (isSingleInboxUser ? 'Email' : `${selectedStateFull} Inbox`))
          : 'Email'),
    leftSlot: (selectedThread || selectedState) ? (
      <button
        onClick={() => {
          // iter122 — On desktop the thread detail lives inside the
          // mailbox split-pane, so the page didn't actually change.
          // Skip the "back to mailbox" intermediate step and jump
          // straight to the list of mailboxes — that's what the user
          // intends.
          if (selectedThread && isDesktopLandscape) {
            setSelectedThread(null); setMessages([]);
            setSelectedState(null); setShowQueue(false); setShowMonitor(false);
            setSearchParams({}, { replace: true });
            return;
          }
          if (selectedThread) { setSelectedThread(null); setMessages([]); }
          else { setSelectedState(null); setShowQueue(false); setSearchParams({}, { replace: true }); }
        }}
        className="w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center"
        aria-label="Back"
        data-testid="email-back-btn"
      ><ArrowLeft className="w-5 h-5" /></button>
    ) : null,
    rightSlot: selectedState && !showQueue ? (
      <button
        onClick={() => {
          setComposeReplyTo(null);
          setComposeDraft(null);
          // iter122 — Desktop: open the composer INSIDE the right
          // pane rather than as a full-screen bottom sheet.
          // iter123 — Also auto-collapse the thread-list pane so the
          // compose card has the full card width. User can re-open
          // the pane via the pill toggle. Resets after compose closes.
          if (isDesktopLandscape) {
            setSelectedThread(null);
            setMessages([]);
            setLeftPaneCollapsed(true);
            setInlineReplyOpen(true);
          } else {
            setComposeOpen(true);
          }
        }}
        className="w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center"
        aria-label="Compose"
        data-testid="email-compose-btn"
      ><Plus className="w-5 h-5" /></button>
    ) : null,
  }, [selectedThread, selectedState, showQueue, isDesktopLandscape, setSearchParams]);

  // Load inboxes
  const loadInboxes = useCallback(async () => {
    try {
      // iter172 — When mounted by the dedicated Email Verifications
      // admin page, pass include_system=1 so the verify@ mailbox
      // shows up in the inbox list response. Outside that page the
      // backend hides system mailboxes from /email/inboxes.
      const url = includeSystem ? '/email/inboxes?include_system=1' : '/email/inboxes';
      const { data } = await api.get(url);
      setInboxes(data || []);
      // Auto-select forced state if present, otherwise auto-select
      // when there's exactly one mailbox available.
      if (forceStateCode) {
        setSelectedState(forceStateCode);
      } else if (!selectedState && data?.length === 1) {
        setSelectedState(data[0].state);
      }
    } catch (e) {
      setInboxes([]);
      toast.error(e?.response?.data?.detail || 'Could not load inboxes');
    }
  }, [api, selectedState, forceStateCode, includeSystem]);

  useEffect(() => { loadInboxes(); }, [loadInboxes]);

  // Load threads (or admin queue / sent / drafts)
  const loadThreads = useCallback(async (opts = {}) => {
    if (!selectedState) return;
    // Background polls (silent=true) skip the loading flag so the
    // list doesn't flash a skeleton/spinner every 30s.
    const silent = !!opts.silent;
    if (!silent) setThreadsLoading(true);
    try {
      const url = showMonitor
        ? `/email/monitor/${selectedState}`
        : showQueue
          ? `/email/admin-queue/${selectedState}`
          : folder === 'sent'
            ? `/email/inboxes/${selectedState}/sent`
            : `/email/inboxes/${selectedState}/threads`;
      const { data } = await api.get(url);
      setThreads((prev) => {
        const next = data || [];
        // Avoid re-rendering the entire list when the polled data
        // is identical — prevents UI jolt from React replacing the
        // array reference.
        if (prev.length === next.length) {
          let same = true;
          for (let i = 0; i < prev.length; i++) {
            const a = prev[i], b = next[i];
            if (!a || !b || a.id !== b.id || a.last_message_at !== b.last_message_at || a.unread !== b.unread || a.subject !== b.subject) {
              same = false;
              break;
            }
          }
          if (same) return prev;
        }
        return next;
      });
    } catch (e) {
      if (!silent) {
        toast.error(e?.response?.data?.detail || 'Could not load threads');
        setThreads([]);
      }
    } finally {
      if (!silent) setThreadsLoading(false);
    }
  }, [api, selectedState, showQueue, showMonitor, folder]);

  // Load this user's drafts for the selected state.
  const loadDrafts = useCallback(async () => {
    if (!selectedState) return;
    try {
      const { data } = await api.get(`/email/inboxes/${selectedState}/drafts`);
      setDrafts(data || []);
    } catch (_e) { /* silent */ }
  }, [api, selectedState]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  // Load this user's drafts whenever they switch into the Drafts folder
  // for the current state.
  useEffect(() => {
    if (selectedState && folder === 'drafts') loadDrafts();
  }, [selectedState, folder, loadDrafts]);

  // Poll thread list every 30s (lightweight, silent — no skeleton flash)
  useEffect(() => {
    if (!selectedState || selectedThread) return undefined;
    const t = setInterval(() => loadThreads({ silent: true }), 30_000);
    return () => clearInterval(t);
  }, [selectedState, selectedThread, loadThreads]);

  // Poll the open thread's messages every 15s so inbound replies show
  // up automatically without the user pull-to-refreshing.
  useEffect(() => {
    if (!selectedThread) return undefined;
    const refresh = async () => {
      try {
        const { data } = await api.get(`/email/threads/${selectedThread.id}/messages`);
        const next = data || [];
        // Skip state update if the message list is byte-identical
        // (length + ids in same order) — avoids re-render jolts.
        setMessages((prev) => {
          if (prev.length === next.length) {
            let same = true;
            for (let i = 0; i < prev.length; i++) {
              if (!prev[i] || !next[i] || prev[i].id !== next[i].id) {
                same = false;
                break;
              }
            }
            if (same) return prev;
          }
          return next;
        });
      } catch (_e) { /* silent */ }
    };
    const t = setInterval(refresh, 15_000);
    return () => clearInterval(t);
  }, [selectedThread, api]);

  // Load a thread's messages
  const openThread = async (thread) => {
    setSelectedThread(thread);
    setInlineReplyOpen(false);
    setMessages([]);
    setMsgsLoading(true);
    // iter125.4 — ALWAYS mark read on open (don't gate on t.unread —
    // local read overrides should also flip back to read if the
    // user opens the thread again). Also optimistically zero out
    // unread on the row + await the backend so the next loadThreads
    // poll sees the new read_by_X timestamp.
    setReadOverrides((m) => ({ ...m, [thread.id]: true }));
    setThreads((xs) => xs.map((x) => x.id === thread.id ? { ...x, unread: false } : x));
    api.post(`/email/threads/${thread.id}/read`).catch(() => {});
    try {
      const { data } = await api.get(`/email/threads/${thread.id}/messages`);
      setMessages(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load thread');
    } finally {
      setMsgsLoading(false);
    }
  };

  // Deep-link: open a thread from ?thread=
  useEffect(() => {
    const tid = searchParams.get('thread');
    if (tid && threads.length && !selectedThread) {
      const t = threads.find((x) => x.id === tid);
      if (t) openThread(t);
    }
  }, [searchParams, threads, selectedThread]);

  // Archive helper
  const archiveThread = async () => {
    if (!selectedThread) return;
    try {
      await api.post(`/email/threads/${selectedThread.id}/archive`);
      toast.success('Thread archived');
      setSelectedThread(null); setMessages([]);
      loadThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Archive failed');
    }
  };

  // Pull-to-refresh — refreshes the current view (inboxes / threads
  // / messages) based on which one is active.
  const refreshAll = useCallback(async () => {
    if (selectedThread) {
      try {
        const { data } = await api.get(`/email/threads/${selectedThread.id}/messages`);
        setMessages(data || []);
      } catch (_e) {/* surface errors via toast in callers */}
      await loadThreads();
    } else if (selectedState) {
      await loadThreads();
    } else {
      await loadInboxes();
    }
  }, [api, selectedThread, selectedState, loadThreads, loadInboxes]);
  const { pullY, refreshing } = usePullToRefresh({ onRefresh: refreshAll });

  // ─────────── View: No inboxes ───────────
  if (inboxes === null) {
    return <PageLoader label="Loading email..." testId="email-loading" />;
  }

  if (!inboxes.length) {
    return (
      <div className="px-6 py-10 text-center" data-testid="email-no-access">
        <div className="w-16 h-16 rounded-full bg-muted/40 mx-auto flex items-center justify-center mb-4">
          <Mail className="w-7 h-7 text-muted-foreground" />
        </div>
        <h2 className="text-lg font-semibold mb-1">No inbox access yet</h2>
        <p className="text-sm text-muted-foreground max-w-xs mx-auto">
          You'll get access to your state's shared inbox once you complete onboarding.
        </p>
      </div>
    );
  }

  // ─────────── View 1: Inbox Picker ───────────
  if (!selectedState) {
    // Admin search filter over the state inbox list
    const q = (inboxSearch || '').trim().toLowerCase();
    const shown = !q
      ? inboxes
      : inboxes.filter((ib) => {
          const full = (STATE_CODE_TO_NAME[ib.state] || '').toLowerCase();
          return (
            ib.state.toLowerCase().includes(q) ||
            full.includes(q) ||
            (ib.email_address || '').toLowerCase().includes(q)
          );
        });
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] px-4 py-3 md:pt-2 space-y-2" data-testid="email-inbox-list">
        <PullToRefreshIndicator pullY={pullY} refreshing={refreshing} />
        {user?.role === 'admin' && (
          <div className="relative mb-2" data-testid="email-inbox-search-wrap">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
            <Input
              value={inboxSearch}
              onChange={(e) => setInboxSearch(e.target.value)}
              placeholder="Search state or email…"
              className="pl-9 h-9 text-sm"
              data-testid="email-inbox-search"
            />
          </div>
        )}
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground px-2 mb-1">
          {inboxes.length === 1 ? 'Your inbox' : 'Your inboxes'}
        </p>
        {shown.map((ib) => {
          const full = STATE_CODE_TO_NAME[ib.state] || ib.state;
          return (
            <button
              key={ib.id}
              onClick={() => { setSelectedState(ib.state); setSearchParams({ state: ib.state }, { replace: true }); }}
              className="w-full flex items-center gap-3 p-3 rounded-xl border bg-white dark:bg-[#1a1a1a] hover:bg-muted/60 text-left"
              data-testid={`email-inbox-card-${ib.state}`}
            >
              <div className="w-11 h-11 rounded-full bg-[#586D52]/15 text-[#586D52] flex items-center justify-center font-semibold text-sm">
                {ib.state}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm">{full} Inbox</p>
                <p className="text-xs text-muted-foreground truncate font-mono">{ib.email_address}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
          );
        })}
        {!shown.length && (
          <p className="text-center text-sm text-muted-foreground py-6">No inboxes match "{q}"</p>
        )}
      </div>
    );
  }

  // iter122 — Extracted View 3 (Thread Detail) into a renderable
  // node so it can be reused as the desktop split-pane right side.
  // On mobile/iPad portrait we early-return below; on desktop
  // landscape we drop it into the grid as the right pane.
  const threadDetailNode = selectedThread ? (
      <div
        className="flex flex-col bg-transparent"
        data-testid="email-thread-detail"
        style={{ height: isDesktopLandscape ? '100%' : 'auto', minHeight: isDesktopLandscape ? undefined : 'calc(100vh - 4rem)' }}
      >
        <PullToRefreshIndicator pullY={pullY} refreshing={refreshing} />
        {/* Contact + actions header */}
        <div className="px-4 py-3 border-b space-y-1 flex-shrink-0 bg-background">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">{selectedThread.external_name || selectedThread.external_email}</span>
                {selectedThread.external_name && <span className="ml-1">&lt;{selectedThread.external_email}&gt;</span>}
              </p>
              {selectedThread.assigned_user_name && (
                <p className="text-[11px] text-muted-foreground mt-0.5">Assigned: {selectedThread.assigned_user_name}</p>
              )}
            </div>
            <div className="flex gap-1 flex-shrink-0">
              {canViewAll && (
                <button onClick={() => setReassignOpen(true)} className="p-2 rounded-full hover:bg-muted" aria-label="Reassign" data-testid="email-thread-reassign-btn">
                  <UserPlus className="w-4 h-4" />
                </button>
              )}
              <button onClick={archiveThread} className="p-2 rounded-full hover:bg-muted" aria-label="Archive" data-testid="email-thread-archive-btn">
                <Archive className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Messages — INDEPENDENT scroller. On desktop the right pane
            has its own height (100vh - header), so the messages
            container scrolls separately from the left thread list. */}
        <div
          className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
          style={!isDesktopLandscape ? { paddingBottom: 'calc(3.5rem + 5rem + env(safe-area-inset-bottom, 0px))' } : undefined}
          data-testid="email-messages-scroller"
        >
          {msgsLoading && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>}
          {messages.map((m) => (
            <div
              key={m.id}
              className={cls(
                'rounded-xl p-3 max-w-[92%]',
                m.direction === 'outbound'
                  ? 'bg-[#586D52]/10 ml-auto text-gray-900 dark:bg-[#3a4a36] dark:text-white'
                  : 'bg-muted/60 dark:text-white',
              )}
              data-testid={`email-message-${m.id}`}
            >
              <div className="flex items-center gap-2 mb-1 text-xs">
                <span className="font-semibold">{m.sender_display_name || m.from_display_name || m.from_email}</span>
                <span className="text-muted-foreground">{when(m.created_at)}</span>
              </div>
              <EmailMessageBody html={m.body_html} text={m.body_html ? '' : stripHtml(m.body)} />
              {m.has_attachments && m.attachments?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {m.attachments.map((a) => (
                    <a
                      key={a.id}
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/email/messages/${m.id}/attachments/${a.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 text-xs bg-white/70 dark:bg-[#0f0f0f]/70 rounded px-2 py-1.5 hover:underline"
                    >
                      <Paperclip className="w-3.5 h-3.5" />
                      <span className="truncate">{a.file_name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
          {/* iter122 — Inline composer renders here on desktop so the
              reply visually sits at the bottom of the conversation. */}
          {isDesktopLandscape && inlineReplyOpen && (
            <ComposeSheet
              open={true}
              inline={true}
              onClose={() => setInlineReplyOpen(false)}
              api={api}
              stateCode={selectedState}
              inboxAddress={selectedInbox?.email_address}
              replyTo={{ thread_id: selectedThread.id, external_email: selectedThread.external_email, subject: selectedThread.subject }}
              editingDraft={null}
              onDraftChanged={loadDrafts}
              onSent={() => { setInlineReplyOpen(false); openThread(selectedThread); loadThreads(); }}
              user={user}
            />
          )}
        </div>

        {selectedThread?.identity_help_request_token && (
          <OverrideRequestCircle threadId={selectedThread.id} api={api} currentUserId={user?.id} />
        )}

        {/* iter122 — Reply bar is ALWAYS VISIBLE as a footer of the
            right pane. On mobile it's anchored above the bottom nav.
            On desktop it sits stickied at the bottom of the
            independent right-pane scroller. */}
        <div
          className={cls(
            'bg-background border-t px-4 py-2 flex-shrink-0',
            !isDesktopLandscape && 'fixed left-0 right-0 z-30',
          )}
          style={!isDesktopLandscape ? { bottom: 'calc(env(safe-area-inset-bottom, 0px) + 4rem)' } : undefined}
          data-testid="email-thread-reply-bar"
        >
          {/* iter122 — Cap the Reply button width on desktop so it
              doesn't stretch into a banner when the thread list is
              collapsed. Mobile keeps the full-width press target. */}
          <div className={isDesktopLandscape ? 'max-w-md mx-auto' : ''}>
            <Button
              onClick={() => {
                if (isDesktopLandscape) {
                  setInlineReplyOpen(true);
                  setTimeout(() => {
                    const el = document.querySelector('[data-testid="email-messages-scroller"]');
                    if (el) el.scrollTop = el.scrollHeight;
                  }, 80);
                } else {
                  setComposeReplyTo({ thread_id: selectedThread.id, external_email: selectedThread.external_email, subject: selectedThread.subject });
                  setComposeOpen(true);
                }
              }}
              className="w-full bg-[#586D52] hover:bg-[#4a5d46]"
              data-testid={isDesktopLandscape ? 'email-thread-reply-btn-desktop' : 'email-thread-reply-btn'}
              disabled={isDesktopLandscape && inlineReplyOpen}
            >
              <Send className="w-4 h-4 mr-1.5" /> {isDesktopLandscape && inlineReplyOpen ? 'Replying…' : 'Reply'}
            </Button>
          </div>
        </div>

        <ComposeSheet
          open={composeOpen}
          onClose={() => { setComposeOpen(false); setComposeDraft(null); }}
          api={api}
          stateCode={selectedState}
          inboxAddress={selectedInbox?.email_address}
          replyTo={composeReplyTo}
          editingDraft={composeDraft}
          onDraftChanged={loadDrafts}
          onSent={() => { openThread(selectedThread); loadThreads(); }}
          user={user}
        />
        <ReassignSheet
          open={reassignOpen}
          onClose={() => setReassignOpen(false)}
          api={api}
          threadId={selectedThread.id}
          stateCode={selectedState}
          currentUserId={selectedThread.assigned_user_id}
          onReassigned={() => { loadThreads(); setSelectedThread(null); setMessages([]); }}
        />
      </div>
    ) : null;

  // ─────────── View 3 (mobile / iPad portrait early return) ───────────
  if (selectedThread && !isDesktopLandscape) {
    return threadDetailNode;
  }

  // ─────────── View 2: Thread list (or Admin Queue) ───────────
  const q = (threadSearch || '').trim().toLowerCase();
  let shownThreads = !q
    ? threads
    : threads.filter((t) =>
        `${t.external_name || ''} ${t.external_email || ''} ${t.subject || ''} ${t.last_message_preview || ''} ${t.assigned_user_name || ''}`
          .toLowerCase()
          .includes(q)
      );
  // iter125.2 — Monitor + Queue views are admin oversight surfaces;
  // they MUST show every thread regardless of the user's own local
  // hide/flag/read overrides. Without this guard, hiding a thread
  // in the Inbox view would also vanish it from Monitor, which is
  // wrong (and is why the user was missing threads).
  if (!showMonitor && !showQueue) {
    shownThreads = shownThreads.filter((t) => {
      const isHidden = hiddenThreadIds.has(t.id);
      if (filterHidden) {
        if (!isHidden) return false;
      } else {
        // Hide-by-default rule: hidden threads are not shown in any
        // other view unless the filter chip is explicitly ON.
        if (isHidden) return false;
      }
      if (filterFlagged && !flaggedThreadIds.has(t.id)) return false;
      if (filterUnread) {
        const reallyUnread = !!t.unread && !readOverrides[t.id];
        if (!reallyUnread) return false;
      }
      return true;
    });
  }
  return (
    <div
      className="fixed inset-x-0 overflow-hidden bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000]"
      style={{
        top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))',
        bottom: 'calc(var(--app-bottom-h, 4rem) + env(safe-area-inset-bottom, 0px))',
      }}
      data-testid="email-page-wrapper"
      data-email-collapsed-cap={isDesktopLandscape ? 'true' : undefined}
    >
      {/* iter122 — On desktop, wrap the entire split-pane in a single
          rounded card so it matches the Call-pad / Calendar visual
          treatment (rounded edges with spacing). The outer wrapper
          handles the gradient background and side gutters; the card
          owns the internal layout and is the ONLY container that
          scrolls — its two children (left pane + right pane) each
          have independent overflow-y-auto. Mobile / iPad portrait
          skips the card and renders inline.
          iter125.4 — The OUTER fixed wrapper paints the gradient
          edge-to-edge (matches every other page). This INNER
          padding wrapper is the 1200px-capped centered column the
          rounded card lives in — restores the original message
          area width the user had before iter125.3. */}
      <div
        className={cls(
          'h-full mx-auto',
          isDesktopLandscape ? 'p-4 max-w-[1200px]' : 'p-0',
        )}
      >
        <div
          className={cls(
            // Card styles only on desktop landscape.
            isDesktopLandscape && 'h-full rounded-3xl bg-white/95 dark:bg-[#141414]/95 shadow-lg border border-black/5 dark:border-white/5 overflow-hidden',
            isDesktopLandscape && !leftPaneCollapsed ? 'grid' : 'flex flex-col',
            !isDesktopLandscape && 'h-full overflow-hidden',
          )}
          style={isDesktopLandscape && !leftPaneCollapsed ? { gridTemplateColumns: '2fr 4fr' } : undefined}
          data-testid="email-thread-list"
        >
      <div
        className={cls(
          // iter124 — Mobile inherits `h-full overflow-hidden` so the
          // inner thread-list scroller actually has bounded height.
          // Without these, the column grew to its natural content
          // height and nothing scrolled on mobile.
          'flex flex-col h-full overflow-hidden',
          isDesktopLandscape && 'border-r border-black/5 dark:border-white/5',
          isDesktopLandscape && leftPaneCollapsed && 'hidden',
        )}
      >
      <PullToRefreshIndicator pullY={pullY} refreshing={refreshing} />
      {/* Combined tabs row — Inbox / Sent / Drafts (all users) +
          Queue / Monitor (admin/supervisor/monitor users).
          iter122 — All 5 tabs fit on a single row on desktop too —
          no wrap, no horizontal scroll. Text + padding tuned to fit
          inside the 2/6-width left pane. */}
      <div
        className={cls(
          'px-3 py-2 flex flex-wrap gap-1 border-b flex-shrink-0',
          !isDesktopLandscape && 'overflow-x-auto whitespace-nowrap flex-nowrap',
        )}
        data-testid="email-folder-tabs"
      >
        <button
          onClick={() => { setShowQueue(false); setShowMonitor(false); setFolder('inbox'); }}
          className={cls(
            'px-2 py-1 text-[10px] font-semibold rounded-full border transition-colors flex-shrink-0',
            !showQueue && !showMonitor && folder === 'inbox'
              ? 'bg-[#586D52] text-white border-[#586D52]'
              : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 dark:bg-[#1f1f1f] dark:text-gray-300 dark:border-[#2a2a2a]'
          )}
          data-testid="email-folder-inbox"
        ><Inbox className="w-3.5 h-3.5 mr-1 inline" /> Inbox</button>
        <button
          onClick={() => { setShowQueue(false); setShowMonitor(false); setFolder('sent'); }}
          className={cls(
            'px-2 py-1 text-[10px] font-semibold rounded-full border transition-colors flex-shrink-0',
            !showQueue && !showMonitor && folder === 'sent'
              ? 'bg-[#586D52] text-white border-[#586D52]'
              : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 dark:bg-[#1f1f1f] dark:text-gray-300 dark:border-[#2a2a2a]'
          )}
          data-testid="email-folder-sent"
        ><SendHorizonal className="w-3.5 h-3.5 mr-1 inline" /> Sent</button>
        <button
          onClick={() => { setShowQueue(false); setShowMonitor(false); setFolder('drafts'); }}
          className={cls(
            'px-2 py-1 text-[10px] font-semibold rounded-full border transition-colors flex-shrink-0',
            !showQueue && !showMonitor && folder === 'drafts'
              ? 'bg-[#586D52] text-white border-[#586D52]'
              : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 dark:bg-[#1f1f1f] dark:text-gray-300 dark:border-[#2a2a2a]'
          )}
          data-testid="email-folder-drafts"
        ><FileEdit className="w-3.5 h-3.5 mr-1 inline" /> Drafts</button>
        {canViewAll && (
          <button
            onClick={() => { setShowQueue(true); setShowMonitor(false); }}
            className={cls(
              'px-2 py-1 text-[10px] font-semibold rounded-full border transition-colors flex-shrink-0',
              showQueue
                ? 'bg-amber-500 text-white border-amber-500'
                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 dark:bg-[#1f1f1f] dark:text-gray-300 dark:border-[#2a2a2a]'
            )}
            data-testid="email-tab-queue"
          ><AlertCircle className="w-3.5 h-3.5 mr-1 inline" /> Queue</button>
        )}
        {canMonitor && (
          <button
            onClick={() => { setShowQueue(false); setShowMonitor(true); }}
            className={cls(
              'px-2 py-1 text-[10px] font-semibold rounded-full border transition-colors flex-shrink-0',
              showMonitor
                ? 'bg-purple-600 text-white border-purple-600'
                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 dark:bg-[#1f1f1f] dark:text-gray-300 dark:border-[#2a2a2a]'
            )}
            data-testid="email-tab-monitor"
          ><Eye className="w-3.5 h-3.5 mr-1 inline" /> Monitor</button>
        )}
      </div>
      {/* iter124 — Search + filter row, ALL users (was admin-only).
          The funnel button swaps the input for a 3-chip toggle row
          (Unread / Hidden / Flagged) that narrows the visible
          threads inside the current folder. */}
      <div className="px-3 py-2 border-b border-black/5 dark:border-white/5 flex-shrink-0" data-testid="email-thread-search-wrap">
        {filterMode === 'search' ? (
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
              <Input
                value={threadSearch}
                onChange={(e) => setThreadSearch(e.target.value)}
                placeholder="Search"
                className="pl-9 h-9 text-sm"
                data-testid="email-thread-search"
              />
            </div>
            <button
              onClick={() => { setFilterMode('filter'); setThreadSearch(''); }}
              className="h-9 w-9 rounded-full flex items-center justify-center border border-[#586D52]/30 bg-white dark:bg-[#1a1a1a] text-[#586D52] hover:bg-[#586D52]/10 flex-shrink-0"
              aria-label="Show filters"
              data-testid="email-filter-toggle"
            >
              <SlidersHorizontal className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            {[
              { key: 'unread', label: 'Unread', on: filterUnread, set: setFilterUnread },
              { key: 'hidden', label: 'Hidden', on: filterHidden, set: setFilterHidden },
              { key: 'flagged', label: 'Flagged', on: filterFlagged, set: setFilterFlagged },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => f.set((v) => !v)}
                className={cls(
                  'flex-1 h-9 rounded-full text-xs font-semibold border transition-colors',
                  f.on
                    ? 'bg-[#586D52] text-white border-[#586D52]'
                    : 'bg-white dark:bg-[#1a1a1a] text-[#586D52] border-[#586D52]/30 hover:bg-[#586D52]/10',
                )}
                data-testid={`email-filter-${f.key}`}
              >{f.label}</button>
            ))}
            <button
              onClick={() => { setFilterMode('search'); setFilterUnread(false); setFilterHidden(false); setFilterFlagged(false); }}
              className="h-9 w-9 rounded-full flex items-center justify-center border border-[#586D52]/30 bg-white dark:bg-[#1a1a1a] text-[#586D52] hover:bg-[#586D52]/10 flex-shrink-0"
              aria-label="Back to search"
              data-testid="email-filter-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      {threadsLoading && (
        <PageLoader label="Loading email..." fullScreen={false} gradient={false} testId="email-threads-loading" />
      )}
      {/* iter122 — Threads + drafts list lives inside its own padded
          scroll-wrapper so the cards have breathing room on the
          left/right edges (mirrors the admin landing inbox cards).
          iter125 — `touch-pan-y` + `-webkit-overflow-scrolling:touch`
          + min-h-0 so iOS Safari actually scrolls on mobile (was
          blocking vertical pan inside the fixed-positioned page). */}
      <div
        className={cls(
          'flex-1 overflow-y-auto min-h-0 touch-pan-y',
          isDesktopLandscape ? 'px-3 py-2' : 'px-2 py-2',
        )}
        style={{ WebkitOverflowScrolling: 'touch' }}
        data-testid="email-thread-list-scroller"
      >
      {/* Drafts folder — render the user's saved drafts; tapping
          one re-opens the compose sheet with the prefilled fields,
          and the inline trash button discards the draft. */}
      {!showQueue && !showMonitor && folder === 'drafts' && (
        drafts.length === 0 ? (
          <div className="py-10 text-center" data-testid="email-empty-drafts">
            <div className="w-14 h-14 rounded-full bg-muted/40 mx-auto flex items-center justify-center mb-3">
              <FileEdit className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium mb-1">No drafts</p>
            <p className="text-xs text-muted-foreground max-w-xs mx-auto">
              Drafts auto-save every couple of seconds while you're typing a new message.
            </p>
          </div>
        ) : (
          drafts.map((d) => {
            const previewBody = stripHtml(d.body || '').trim() || '(empty draft)';
            const recipient = (d.to || '').trim() || '(no recipient)';
            const subject = d.subject || '(no subject)';
            const recipientInitials = initials(null, recipient);
            const draftActions = [
              {
                key: 'delete',
                icon: <Trash2 className="w-4 h-4" />,
                label: 'Delete',
                bg: 'bg-red-600',
                onClick: async () => {
                  try {
                    await api.delete(`/email/drafts/${d.id}`);
                    setDrafts((xs) => xs.filter((x) => x.id !== d.id));
                  } catch (_e) { toast.error('Could not delete draft'); }
                },
              },
            ];
            return (
              <SwipeableEmailRow
                key={d.id}
                id={`draft-${d.id}`}
                actions={draftActions}
                offset={swipeOffsets[`draft-${d.id}`] || 0}
                setOffset={setSwipeOffset}
                testId={`email-draft-row-${d.id}`}
              >
                <button
                  onClick={() => {
                    if (isDesktopLandscape) {
                      setSelectedThread(null);
                      setMessages([]);
                      setComposeDraft(d);
                      setComposeReplyTo(null);
                      setInlineReplyOpen(true);
                    } else {
                      setComposeDraft(d);
                      setComposeReplyTo(null);
                      setComposeOpen(true);
                    }
                  }}
                  className={cls(
                    // iter125 — EXACT same className as inbox/sent card. Solid white.
                    'flex items-start gap-3 px-3 py-3 rounded-2xl border text-left w-full transition-all shadow-sm relative',
                    'bg-white hover:bg-white dark:bg-[#141414] dark:hover:bg-[#1a1a1a] border-black/10 dark:border-white/10',
                  )}
                >
                  <div className="w-10 h-10 rounded-full bg-[#586D52]/15 text-[#586D52] flex items-center justify-center font-semibold text-sm flex-shrink-0 mt-0.5">
                    {recipientInitials || <FileEdit className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold truncate flex-1">{recipient}</p>
                      <span className="text-[11px] text-muted-foreground flex-shrink-0">{when(d.updated_at)}</span>
                    </div>
                    <p className="text-sm truncate text-foreground/90 mt-0.5">{subject}</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{previewBody}</p>
                  </div>
                </button>
              </SwipeableEmailRow>
            );
          })
        )
      )}
      {!threadsLoading && folder !== 'drafts' && shownThreads.length === 0 && (
        <div className="py-10 text-center" data-testid="email-empty-state">
          <div className="w-14 h-14 rounded-full bg-muted/40 mx-auto flex items-center justify-center mb-3">
            {showQueue ? <AlertCircle className="w-6 h-6 text-muted-foreground" /> : folder === 'sent' ? <SendHorizonal className="w-6 h-6 text-muted-foreground" /> : <Inbox className="w-6 h-6 text-muted-foreground" />}
          </div>
          <p className="text-sm font-medium mb-1">
            {threadSearch ? 'No matches' : showQueue ? 'Queue is empty' : folder === 'sent' ? 'Nothing sent yet' : 'No messages'}
          </p>
          <p className="text-xs text-muted-foreground max-w-xs mx-auto">
            {threadSearch
              ? `Nothing matches "${threadSearch}".`
              : showQueue
                ? 'Emails with unknown routing tokens will appear here for triage.'
                : folder === 'sent'
                  ? 'Messages you send will show up here.'
                  : 'Start a new conversation with the + button in the header.'}
          </p>
        </div>
      )}
      {folder !== 'drafts' && shownThreads.map((t) => {
        // Safe fallbacks — queue rows often lack a display-name or
        // preview and without them the row gapped awkwardly.
        const contact = t.external_name || t.external_email || 'Unknown sender';
        const subject = t.subject || '(no subject)';
        const preview = t.last_message_preview || '…';
        // Unread highlight: pale-blue tinted row + thicker left bar +
        // bold subject. Only applies in the Inbox folder; Sent rows
        // are always read.
        const localRead = readOverrides[t.id];
        const effectiveUnread = folder === 'inbox' && (localRead === false || (!!t.unread && localRead !== true));
        const isFlagged = flaggedThreadIds.has(t.id);
        const isHiddenRow = hiddenThreadIds.has(t.id);
        const rowActions = [
          {
            key: 'read',
            icon: effectiveUnread ? <MailOpen className="w-4 h-4" /> : <Mail className="w-4 h-4" />,
            label: effectiveUnread ? 'Read' : 'Unread',
            bg: 'bg-[#9aa893]',
            onClick: () => toggleReadOverride(t.id, !!t.unread),
          },
          {
            key: 'hide',
            icon: isHiddenRow ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />,
            label: isHiddenRow ? 'Unhide' : 'Hide',
            bg: 'bg-gray-700',
            onClick: () => toggleHidden(t.id),
          },
          {
            key: 'flag',
            icon: <Flag className={cls('w-4 h-4', isFlagged && 'fill-current')} />,
            label: isFlagged ? 'Unflag' : 'Flag',
            bg: 'bg-amber-500',
            onClick: () => toggleFlagged(t.id),
          },
        ];
        return (
          <SwipeableEmailRow
            key={t.id}
            id={t.id}
            actions={rowActions}
            offset={swipeOffsets[t.id] || 0}
            setOffset={setSwipeOffset}
            testId={`email-thread-row-${t.id}`}
          >
            <button
              onClick={() => openThread(t)}
              className={cls(
                // iter125 — Solid backgrounds (no /90 transparency)
                // so the card list doesn't look "see-through" against
                // the page gradient.
                'flex items-start gap-3 px-3 py-3 rounded-2xl border text-left w-full transition-all shadow-sm relative',
                selectedThread?.id === t.id
                  ? 'bg-white dark:bg-[#141414] border-[#586D52] ring-2 ring-[#586D52]/40'
                  : effectiveUnread
                    // iter125 — Unread = sage-green linear gradient
                    // card. White text on the gradient so the subject
                    // and preview are still legible.
                    ? 'bg-gradient-to-br from-[#7a8e74] via-[#6b8064] to-[#586D52] hover:from-[#85997f] hover:via-[#728668] hover:to-[#637859] border-[#586D52] text-white dark:from-[#3f5a3a] dark:via-[#34492f] dark:to-[#2a3a25] dark:border-[#586D52]'
                    : 'bg-white hover:bg-white dark:bg-[#141414] dark:hover:bg-[#1a1a1a] border-black/10 dark:border-white/10',
              )}
            >
              <div className={cls(
                'w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0 mt-0.5',
                effectiveUnread
                  ? 'bg-white/25 text-white'
                  : 'bg-[#586D52]/15 text-[#586D52]',
              )}>
                {initials(t.external_name, t.external_email)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className={cls('text-sm truncate flex-1', effectiveUnread ? 'font-bold text-white' : 'font-semibold')}>{contact}</p>
                  {isFlagged && <Flag className={cls('w-3 h-3 fill-current flex-shrink-0', effectiveUnread ? 'text-amber-300' : 'text-amber-500')} />}
                  {effectiveUnread && <span className="w-2 h-2 rounded-full bg-white flex-shrink-0" aria-label="Unread" />}
                  <span className={cls('text-[11px] flex-shrink-0', effectiveUnread ? 'text-white/85' : 'text-muted-foreground')}>{when(t.last_message_at)}</span>
                </div>
                <p className={cls('text-sm truncate mt-0.5', effectiveUnread ? 'font-semibold text-white' : 'text-foreground/90')}>{subject}</p>
                <p className={cls('text-xs truncate mt-0.5', effectiveUnread ? 'text-white/85' : 'text-muted-foreground')}>{preview}</p>
                {canViewAll && t.assigned_user_name && (
                  <p className={cls('text-[10px] uppercase tracking-wider mt-1', effectiveUnread ? 'text-white/90' : 'text-[#586D52]')}>→ {t.assigned_user_name}</p>
                )}
                {t.status === 'unmatched' && (
                  <span className="inline-block mt-1 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">
                    Unmatched
                  </span>
                )}
              </div>
            </button>
          </SwipeableEmailRow>
        );
      })}
      </div>
      </div>
      {/* iter122 — Desktop right-pane: opened thread detail. Mobile
          uses the legacy early-return at the top of this component. */}
      {isDesktopLandscape && (
        <div
          className="overflow-y-auto relative h-full"
          data-testid="email-desktop-right-pane"
        >
          {/* iter122 — Persistent collapse / expand pill. Anchored to
              the LEFT edge of the right pane so the user can always
              pull the thread list back regardless of whether a
              thread is currently selected or which view they
              refreshed into. */}
          <button
            onClick={() => setLeftPaneCollapsed((c) => !c)}
            className="absolute top-1/2 -translate-y-1/2 left-0 z-30 h-12 w-6 rounded-r-full bg-[#586D52] text-white shadow-md flex items-center justify-center hover:bg-[#4a5d46]"
            aria-label={leftPaneCollapsed ? 'Show thread list' : 'Hide thread list'}
            data-testid="email-left-pane-toggle"
          >
            {leftPaneCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
          {threadDetailNode || (
            inlineReplyOpen ? (
              <div className="p-4 h-full">
                <ComposeSheet
                  open={true}
                  inline={true}
                  onClose={() => { setInlineReplyOpen(false); setComposeDraft(null); setLeftPaneCollapsed(false); }}
                  api={api}
                  stateCode={selectedState}
                  inboxAddress={selectedInbox?.email_address}
                  replyTo={null}
                  editingDraft={composeDraft}
                  onDraftChanged={loadDrafts}
                  onSent={() => { setInlineReplyOpen(false); setComposeDraft(null); setLeftPaneCollapsed(false); loadThreads(); loadDrafts(); }}
                  user={user}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full min-h-[60vh] text-sm text-muted-foreground px-6 text-center" data-testid="email-desktop-empty-pane">
                Select a conversation on the left to read it here.
              </div>
            )
          )}
        </div>
      )}
        </div>
      </div>
      <ComposeSheet
        open={composeOpen}
        onClose={() => { setComposeOpen(false); setComposeDraft(null); }}
        api={api}
        stateCode={selectedState}
        inboxAddress={selectedInbox?.email_address}
        replyTo={composeReplyTo}
        editingDraft={composeDraft}
        onDraftChanged={loadDrafts}
        onSent={() => { loadThreads(); }}
        user={user}
      />
    </div>
  );
}
